/**
 * Endpoint allowlists for PRIVATE companies (source of truth: backend).
 *
 * The backend rejects (403) any endpoint outside these sets for a private
 * account. We mirror them here so the frontend never fires a request it knows
 * will be refused:
 *
 *  - PRIVATE_ONBOARDING_ENDPOINTS — the narrow set available while the account
 *    is still gated (KYC not yet approved). Everything else 403s.
 *  - PRIVATE_ALLOWED_ENDPOINTS — the full set available once onboarding is done.
 *
 * Anything NOT private (regular companies, pre-login public calls, superadmin)
 * is never restricted here.
 */

export const PRIVATE_ALLOWED_ENDPOINTS = [
	"users/logout",
	"users/get/me",
	"users/profile/update",
	"users/password/change",
	"users/2fa/enable",
	"users/2fa/disable",
	"companies/get/me",
	"companies/get",
	"companies/referral",
	"companies/issuers",
	"companies/transactions",
	"companies/transactions/cards",
	"companies/transactions/all",
	"companies/transactions/card",
	"companies/transactions/filters",
	"companies/transactions/presets",
	"companies/transactions/presets/create",
	"companies/transactions/presets/update",
	"companies/transactions/presets/delete",
	"companies/transactions/account",
	"companies/transactions/details",
	"companies/stats/overview",
	"companies/stats/by-card",
	"companies/stats/spend-overview",
	"companies/stats/cards-spend-overview",
	"cards/create",
	"cards/list",
	"cards/get",
	"cards/update",
	"cards/name/get",
	"cards/name/set",
	"cards/limits/get",
	"cards/limits/set",
	"cards/transactions",
	"cards/topup",
	"cards/topup/get",
	"cards/topup/list",
	"cards/withdraw",
	"cards/withdraw/get",
	"cards/withdraw/list",
	"cards/3ds/list",
	"cards/3ds/refresh",
	"cards/freeze",
	"cards/unfreeze",
	"cards/block",
	"cards/block/preview",
	"cards/block/status",
	"cards/tx-control",
	"cardholders/register",
	"cardholders/update",
	"cardholders/list",
	"cardholders/get",
	"cardholders/form",
	"cardholders/submit",
	"cardholders/uploadDocument/:uid",
	"cardholders/check/status",
	"kyc/start",
	"kyc/status",
	"kyc/issuers",
	"kyc/phone",
	"kyc/phone/get",
	"crypto/coins",
	"crypto/address",
	"crypto/quote",
	"crypto/deposits",
	"common/cities",
	"common/countries",
	"common/dialCodes",
	"common/cardCurrencies",
	"common/uploadDocumentTypes",
	"common/uploadDocumentSubTypes",
	"common/cardNetworks",
	"common/cardTypes",
] as const

export const PRIVATE_ONBOARDING_ENDPOINTS = [
	"users/logout",
	"users/get/me",
	"users/profile/update",
	"users/password/change",
	"users/2fa/enable",
	"users/2fa/disable",
	"companies/get/me",
	"companies/issuers",
	"kyc/start",
	"kyc/status",
	"kyc/issuers",
	"kyc/phone",
	"kyc/phone/get",
	"common/countries",
	"common/cities",
	"common/dialCodes",
] as const

const escapeRegex = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

// Turn an allowlist entry into a full-match regex, treating ":param" segments
// as a single wildcard path segment (e.g. cardholders/uploadDocument/:uid).
const toMatcher = (entry: string): RegExp =>
	new RegExp(
		"^" +
			entry
				.split("/")
				.map(seg => (seg.startsWith(":") ? "[^/]+" : escapeRegex(seg)))
				.join("/") +
			"$",
	)

const ALLOWED_MATCHERS = PRIVATE_ALLOWED_ENDPOINTS.map(toMatcher)
const ONBOARDING_MATCHERS = PRIVATE_ONBOARDING_ENDPOINTS.map(toMatcher)

/** Reduce a request "method" to its bare endpoint path (drop base, query, hash). */
export const normalizeEndpoint = (method: string): string => {
	let path = method.split("?")[0].split("#")[0].trim()
	// Strip an accidental absolute/base prefix down to the api path.
	const apiIdx = path.indexOf("/api/v2/")
	if (apiIdx !== -1) path = path.slice(apiIdx + "/api/v2/".length)
	if (path.startsWith("/")) path = path.slice(1)
	return path
}

/**
 * Whether a private account may call `method` right now. `gated` selects the
 * narrower onboarding allowlist. An unclassifiable/empty path is allowed
 * through (fail-open) rather than risk blocking something legitimate.
 */
export const isPrivateEndpointAllowed = (method: string, gated: boolean): boolean => {
	const path = normalizeEndpoint(method)
	if (!path) return true
	const matchers = gated ? ONBOARDING_MATCHERS : ALLOWED_MATCHERS
	return matchers.some(re => re.test(path))
}
